import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import PublishIcon from '@mui/icons-material/Publish'
import SaveIcon from '@mui/icons-material/Save'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AddIcon from '@mui/icons-material/Add'
import MDEditor from '@uiw/react-md-editor'
import SEO from '../utils/seo'
import CrossPublishButtons from '../components/CrossPublishButtons'
import AIEnhance from '../components/AIEnhance'
import { useTheme } from '../contexts/ThemeContext'
import { colors, textFieldSx, dialogPaperSx } from '../styles/theme'
import { slugify } from '../utils/format'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const BlogEditor = () => {
  const { slug: editSlug } = useParams()
  const navigate = useNavigate()
  const { isDark } = useTheme()

  const [posts, setPosts] = useState([])
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    tags: '',
    coverImage: '',
    status: 'draft',
  })
  const [content, setContent] = useState('')
  const [editing, setEditing] = useState(null)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, slug: null })
  const [autoSlug, setAutoSlug] = useState(true)

  useEffect(() => {
    fetchPosts()
  }, [])

  useEffect(() => {
    if (editSlug) {
      loadPost(editSlug)
    }
  }, [editSlug])

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/blog-posts`)
      const data = await response.json()
      setPosts(data)
    } catch (error) {
      console.error('Error fetching posts:', error)
    }
  }

  const loadPost = async (slug) => {
    try {
      const response = await fetch(`${API_URL}/blog-posts/${slug}`)
      if (response.ok) {
        const post = await response.json()
        setFormData({
          title: post.title,
          slug: post.slug,
          excerpt: post.excerpt || '',
          tags: post.tags.join(', '),
          coverImage: post.coverImage || '',
          status: post.status,
        })
        setContent(post.content)
        setEditing(post.slug)
        setAutoSlug(false)
      }
    } catch (error) {
      console.error('Error loading post:', error)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => {
      const updated = { ...prev, [name]: value }
      if (name === 'title' && autoSlug && !editing) {
        updated.slug = slugify(value)
      }
      return updated
    })
  }

  const handleSave = async (publishStatus) => {
    const postData = {
      title: formData.title,
      slug: formData.slug,
      content: content,
      excerpt: formData.excerpt || content.slice(0, 160).replace(/[#*_\[\]]/g, ''),
      tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
      status: publishStatus || formData.status,
      coverImage: formData.coverImage,
    }

    try {
      const url = editing
        ? `${API_URL}/blog-posts/${editing}`
        : `${API_URL}/blog-posts`

      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(postData),
      })

      if (response.ok) {
        const savedPost = await response.json()
        const action = publishStatus === 'published' ? 'Published' : (editing ? 'Updated' : 'Created')
        setMessage({ type: 'success', text: `Post ${action.toLowerCase()} successfully!` })
        setEditing(savedPost.slug)
        setFormData(prev => ({ ...prev, status: savedPost.status, slug: savedPost.slug }))
        setAutoSlug(false)
        fetchPosts()

        if (!editing) {
          navigate(`/blog/editor/${savedPost.slug}`, { replace: true })
        }
      } else {
        const err = await response.json()
        throw new Error(err.error || 'Failed to save')
      }
    } catch (error) {
      console.error('Error saving post:', error)
      setMessage({ type: 'error', text: error.message || 'Failed to save post' })
    }
  }

  const resetForm = () => {
    setFormData({ title: '', slug: '', excerpt: '', tags: '', coverImage: '', status: 'draft' })
    setContent('')
    setEditing(null)
    setAutoSlug(true)
    navigate('/blog/editor', { replace: true })
  }

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/blog-posts/${deleteDialog.slug}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        setMessage({ type: 'success', text: 'Post deleted successfully!' })
        if (editing === deleteDialog.slug) {
          resetForm()
        }
        fetchPosts()
      } else {
        throw new Error('Failed to delete')
      }
    } catch (error) {
      console.error('Error deleting post:', error)
      setMessage({ type: 'error', text: 'Failed to delete post' })
    } finally {
      setDeleteDialog({ open: false, slug: null })
    }
  }

  return (
    <>
      <SEO title="Blog Editor" />

      <div className="section">
        <div className="container-custom max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-4xl md:text-5xl font-bold text-off-white">
                Blog Editor
              </h1>
              <div className="flex gap-2">
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  disabled={!editing || formData.status !== 'published'}
                  onClick={() => window.open(`/blog/${editing}`, '_blank')}
                  sx={{
                    borderColor: colors.info, color: colors.info,
                    '&:hover': { borderColor: colors.info, backgroundColor: 'rgba(var(--color-accent-info-rgb), 0.1)' },
                  }}
                >
                  View
                </Button>
                {editing && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={resetForm}
                    sx={{
                      borderColor: colors.secondaryLight, color: colors.secondaryLight,
                      '&:hover': { borderColor: colors.secondaryLight, backgroundColor: 'rgba(var(--color-accent-secondary-rgb), 0.1)' },
                    }}
                  >
                    New Post
                  </Button>
                )}
              </div>
            </div>

            {message.text && (
              <Alert
                severity={message.type}
                onClose={() => setMessage({ type: '', text: '' })}
                sx={{ mb: 4 }}
              >
                {message.text}
              </Alert>
            )}

            {/* Metadata Form */}
            <Card sx={{ backgroundColor: colors.bgCard, mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <TextField
                    fullWidth required label="Post Title" name="title"
                    value={formData.title} onChange={handleChange} sx={textFieldSx}
                  />
                  <TextField
                    fullWidth required label="Slug" name="slug"
                    value={formData.slug}
                    onChange={(e) => { setAutoSlug(false); handleChange(e) }}
                    sx={textFieldSx}
                  />
                </div>

                <TextField
                  fullWidth label="Excerpt (short summary for listing & SEO)" name="excerpt"
                  multiline rows={2}
                  value={formData.excerpt} onChange={handleChange}
                  placeholder="Brief description for blog listing and SEO..."
                  sx={{ ...textFieldSx, mb: 2 }}
                />

                <div className="grid md:grid-cols-2 gap-4">
                  <TextField
                    fullWidth label="Tags (comma-separated)" name="tags"
                    value={formData.tags} onChange={handleChange}
                    placeholder="genai, architecture, dx"
                    sx={textFieldSx}
                  />
                  <TextField
                    fullWidth label="Cover Image URL" name="coverImage"
                    value={formData.coverImage} onChange={handleChange}
                    placeholder="https://example.com/image.jpg"
                    sx={textFieldSx}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Markdown Editor */}
            <Card sx={{ backgroundColor: colors.bgCard, mb: 4 }}>
              <CardContent sx={{ p: 0 }}>
                <div data-color-mode={isDark ? 'dark' : 'light'}>
                  <MDEditor
                    value={content}
                    onChange={setContent}
                    height={500}
                    preview="live"
                    visibleDragbar={true}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 items-center mb-8">
              <AIEnhance
                content={content}
                title={formData.title}
                onAccept={(enhanced) => setContent(enhanced)}
              />

              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => handleSave('draft')}
                disabled={!formData.title || !formData.slug}
                sx={{
                  backgroundColor: colors.textMuted, color: colors.textOnAccent, fontWeight: 700,
                  '&:hover': { backgroundColor: colors.textFaint },
                }}
              >
                Save Draft
              </Button>

              <Button
                variant="contained"
                startIcon={<PublishIcon />}
                onClick={() => handleSave('published')}
                disabled={!formData.title || !formData.slug}
                sx={{
                  backgroundColor: colors.accent, color: colors.textOnAccent, fontWeight: 700,
                  '&:hover': { backgroundColor: colors.accentHover },
                }}
              >
                {formData.status === 'published' ? 'Update & Publish' : 'Publish'}
              </Button>

              {editing && formData.status === 'published' && (
                <CrossPublishButtons slug={editing} content={content} title={formData.title} />
              )}
            </div>

            {/* All Posts List */}
            <h2 className="text-2xl font-bold text-off-white mb-4">All Posts</h2>
            <div className="space-y-3">
              {posts.map(post => (
                <Card
                  key={post.slug}
                  sx={{
                    backgroundColor: editing === post.slug ? colors.bgElevated : colors.bgCard,
                    border: editing === post.slug ? `1px solid ${colors.borderAccent}` : '1px solid transparent',
                    '&:hover': { backgroundColor: colors.bgElevated },
                    transition: 'all 0.2s',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <div className="flex justify-between items-center">
                      <div className="flex-grow">
                        <div className="flex items-center gap-2 mb-1">
                          <Chip
                            label={post.status}
                            size="small"
                            sx={{
                              backgroundColor: post.status === 'published' ? 'rgba(var(--color-accent-primary-rgb), 0.12)' : post.status === 'draft' ? 'rgba(251, 191, 36, 0.12)' : 'rgba(148, 163, 184, 0.12)',
                              color: post.status === 'published' ? colors.accent : post.status === 'draft' ? colors.warning : colors.textMuted,
                              fontWeight: 600, fontSize: '0.7rem',
                            }}
                          />
                          <span className="text-gray-500 text-xs font-mono">
                            {new Date(post.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                        <h3 className="text-base font-bold text-off-white">{post.title}</h3>
                        {post.excerpt && (
                          <p className="text-gray-400 text-sm mt-1 line-clamp-1">{post.excerpt}</p>
                        )}
                      </div>
                      <div className="flex gap-1 ml-4">
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigate(`/blog/editor/${post.slug}`)
                            loadPost(post.slug)
                          }}
                          sx={{ color: colors.info }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteDialog({ open: true, slug: post.slug })}
                          sx={{ color: colors.error }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {posts.length === 0 && (
                <Card sx={{ backgroundColor: colors.bgCard }}>
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <p className="text-gray-400">No posts yet. Write your first one above!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, slug: null })}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle>Delete Blog Post?</DialogTitle>
        <DialogContent>
          <p className="text-gray-300">
            Are you sure you want to delete this post? This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, slug: null })}
            sx={{ color: colors.textMuted }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            sx={{ color: colors.error, fontWeight: 700 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default BlogEditor
